/**
 * RssFeedDetailModal — View RSS feed details with FormModal pattern.
 * Follows CurrencyDetailModal structure: status card, details card,
 * stats grid, dates card, action buttons.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Rss,
  AlertTriangle,
  ExternalLink,
  Image,
  Clock,
  Bell,
  Tag,
  Calendar,
  RefreshCw,
  RotateCcw,
  Edit3,
  Power,
  PowerOff,
  Eraser,
  Trash2,
  FileText,
} from 'lucide-react';
import { FormModal, MODAL_COLORS, useModal } from '@/components/modal';
import { triggerHapticFeedback } from '../utils/adminAnimations';
import type { RssFeed } from '../hooks/useRssFeeds';

interface RssFeedDetailModalProps {
  feed: RssFeed;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSync: () => void;
  onResync: () => void;
  onPurgePosts: () => void;
  onToggleEnabled: () => void;
  isSyncing: boolean;
  isResyncing: boolean;
  isPurging: boolean;
  isToggling: boolean;
}

function InfoRow({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="flex-shrink-0 w-5 h-5 mt-0.5 text-slate-400">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-neutral-500">{label}</p>
        <p className="text-sm font-medium text-neutral-800 truncate">{value || '—'}</p>
      </div>
    </div>
  );
}

function formatFullDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function formatSyncTime(iso: string | null, t: (key: string) => string): string {
  if (!iso) return t('rssAdmin.neverSynced');
  const d = new Date(iso);
  const now = Date.now();
  const diffMin = Math.floor((now - d.getTime()) / 60000);
  if (diffMin < 1) return t('rssAdmin.justNow');
  if (diffMin < 60) return `${diffMin}m`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h`;
  return d.toLocaleDateString();
}

const cardStyle = {
  backgroundColor: MODAL_COLORS.interactive,
  border: `1px solid ${MODAL_COLORS.border}`,
};

const btnBase = 'flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer';

const neutralBtn = {
  backgroundColor: MODAL_COLORS.interactive,
  border: `1px solid ${MODAL_COLORS.border}`,
};

const dangerBtn = {
  backgroundColor: 'rgba(254, 226, 226, 0.6)',
  border: `1px solid ${MODAL_COLORS.border}`,
  color: '#B91C1C',
};

const amberBtn = {
  backgroundColor: 'rgba(254, 249, 195, 0.6)',
  border: `1px solid ${MODAL_COLORS.border}`,
  color: '#b45309',
};

export function RssFeedDetailModal({
  feed,
  onClose,
  onEdit,
  onDelete,
  onSync,
  onResync,
  onPurgePosts,
  onToggleEnabled,
  isSyncing,
  isResyncing,
  isPurging,
  isToggling,
}: RssFeedDetailModalProps) {
  const { t } = useTranslation();
  const { openConfirm } = useModal();
  const [isOpen, setIsOpen] = useState(true);
  const requestClose = useCallback(() => setIsOpen(false), []);

  const tierLabel =
    feed.notifyTier === 'immediate'
      ? t('rssAdmin.tierImmediate')
      : feed.notifyTier === 'daily'
        ? t('rssAdmin.tierDaily')
        : feed.notifyTier === 'weekly'
          ? t('rssAdmin.tierWeekly')
          : null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={requestClose}
      onExitComplete={onClose}
      title={feed.name}
      maxWidth="max-w-lg"
    >
      <div className="space-y-2">
        {/* Status card */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
              style={{
                background: feed.isEnabled
                  ? 'linear-gradient(135deg, #f97316, #ea580c)'
                  : '#d1d5db',
              }}
            >
              <Rss className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-neutral-800 truncate">{feed.name}</p>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span
                  className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
                  style={{
                    backgroundColor: feed.isEnabled ? '#ecfdf5' : '#fee2e2',
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                  }}
                >
                  {feed.isEnabled ? t('rssAdmin.enabled') : t('rssAdmin.disabled')}
                </span>
                {feed.isBreaking && (
                  <span
                    className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
                    style={{
                      backgroundColor: '#fef3c7',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      color: '#b45309',
                    }}
                  >
                    {t('rssAdmin.breaking')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Sync error alert */}
          {feed.syncError && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg mt-2" style={{ backgroundColor: 'rgba(254, 226, 226, 0.5)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-red-600">{t('rssAdmin.lastError')}</p>
                <p className="text-xs text-red-700 break-all">{feed.syncError}</p>
              </div>
            </div>
          )}
        </div>

        {/* Details card */}
        <div
          className="glass-surface rounded-2xl p-3"
          style={{ border: `1px solid ${MODAL_COLORS.border}` }}
        >
          <InfoRow
            icon={<ExternalLink className="w-4 h-4" />}
            label={t('rssAdmin.feedUrl')}
            value={
              <a
                href={feed.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline break-all"
                dir="ltr"
              >
                {feed.url}
              </a>
            }
          />
          {feed.iconUrl && (
            <div className="flex items-start gap-3 py-2">
              <div className="flex-shrink-0 w-5 h-5 mt-0.5 text-slate-400">
                <Image className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-neutral-500 mb-1.5">{t('rssAdmin.iconUrl')}</p>
                <img
                  src={feed.iconUrl}
                  alt={feed.name}
                  className="h-10 max-w-[160px] object-contain rounded"
                />
              </div>
            </div>
          )}
          <InfoRow
            icon={<Clock className="w-4 h-4" />}
            label={t('rssAdmin.maxPostsPerHour')}
            value={`${feed.maxPostsPerHour}${t('rssAdmin.perHour')}`}
          />
          {feed.notifyTier !== 'none' && tierLabel && (
            <InfoRow
              icon={<Bell className="w-4 h-4" />}
              label={t('rssAdmin.notifyTier')}
              value={tierLabel}
            />
          )}
          {feed.notifyKeywords && feed.notifyKeywords.length > 0 && (
            <InfoRow
              icon={<Tag className="w-4 h-4" />}
              label={t('rssAdmin.keywords')}
              value={feed.notifyKeywords.join(', ')}
            />
          )}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl p-2 text-center" style={cardStyle}>
            <FileText className="w-4 h-4 mx-auto mb-1 opacity-50" />
            <p className="text-xl font-bold">{feed.postCount}</p>
            <p className="text-xs font-semibold opacity-60">{t('rssAdmin.totalPosts')}</p>
          </div>
          <div className="rounded-xl p-2 text-center" style={cardStyle}>
            <Clock className="w-4 h-4 mx-auto mb-1 opacity-50" />
            <p className="text-xl font-bold">{formatSyncTime(feed.lastSyncAt, t)}</p>
            <p className="text-xs font-semibold opacity-60">{t('rssAdmin.lastSynced')}</p>
          </div>
        </div>

        {/* Dates card */}
        <div className="rounded-xl p-3 space-y-1" style={cardStyle}>
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <Calendar className="w-3.5 h-3.5" />
            <span>{t('rssAdmin.createdAt')}: {formatFullDate(feed.createdAt)}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <Clock className="w-3.5 h-3.5" />
            <span>{t('rssAdmin.updatedAt')}: {formatFullDate(feed.updatedAt)}</span>
          </div>
        </div>

        {/* Action buttons */}
        <button
          onClick={() => {
            triggerHapticFeedback();
            onSync();
          }}
          disabled={isSyncing || isResyncing}
          className={`${btnBase} disabled:opacity-50`}
          style={neutralBtn}
        >
          <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
          {t('rssAdmin.syncNow')}
        </button>

        <button
          onClick={() => {
            triggerHapticFeedback();
            openConfirm({
              title: t('rssAdmin.resync'),
              message: t('rssAdmin.resyncConfirm', { name: feed.name, count: feed.postCount }),
              variant: 'danger',
              confirmLabel: t('rssAdmin.resync'),
              onConfirm: () => onResync(),
            });
          }}
          disabled={isResyncing || isSyncing}
          className={`${btnBase} disabled:opacity-50`}
          style={amberBtn}
        >
          <RotateCcw className={`w-4 h-4 ${isResyncing ? 'animate-spin' : ''}`} />
          {t('rssAdmin.resync')}
        </button>

        <button
          onClick={() => {
            triggerHapticFeedback();
            onEdit();
            requestClose();
          }}
          className={btnBase}
          style={neutralBtn}
        >
          <Edit3 className="w-4 h-4" />
          {t('rssAdmin.editFeed')}
        </button>

        <button
          onClick={() => {
            triggerHapticFeedback();
            onToggleEnabled();
          }}
          disabled={isToggling}
          className={`${btnBase} disabled:opacity-50`}
          style={neutralBtn}
        >
          {feed.isEnabled ? (
            <>
              <PowerOff className="w-4 h-4 text-amber-500" />
              {t('rssAdmin.disable')}
            </>
          ) : (
            <>
              <Power className="w-4 h-4 text-green-500" />
              {t('rssAdmin.enable')}
            </>
          )}
        </button>

        {feed.postCount > 0 && (
          <button
            onClick={() => {
              triggerHapticFeedback();
              openConfirm({
                title: t('rssAdmin.deletePosts'),
                message: t('rssAdmin.deletePostsConfirm', { name: feed.name, count: feed.postCount }),
                variant: 'danger',
                confirmLabel: t('common.delete'),
                onConfirm: () => onPurgePosts(),
              });
            }}
            disabled={isPurging}
            className={`${btnBase} disabled:opacity-50`}
            style={dangerBtn}
          >
            <Eraser className={`w-4 h-4 ${isPurging ? 'animate-pulse' : ''}`} />
            {t('rssAdmin.deletePosts')}
          </button>
        )}

        <button
          onClick={() => {
            triggerHapticFeedback();
            openConfirm({
              title: t('rssAdmin.deleteFeed'),
              message: t('rssAdmin.deleteConfirm', { name: feed.name }),
              variant: 'danger',
              confirmLabel: t('common.delete'),
              onConfirm: () => onDelete(),
            });
          }}
          className={btnBase}
          style={dangerBtn}
        >
          <Trash2 className="w-4 h-4" />
          {t('rssAdmin.deleteFeed')}
        </button>
      </div>
    </FormModal>
  );
}
